import clsx from "clsx";
import { Mic, MicOff, Volume2, VolumeX } from "lucide-react";
import { useEffect, useRef } from "react";
import { voice, useVoiceState, useVoiceStatus } from "@/lib/voice/controller";
import { ACTION_BUTTON, ACTION_ICON } from "./actionStyles";

const LABELS: Record<string, string> = {
  listening: "Listening…",
  hearing: "Hearing you…",
  transcribing: "Transcribing…",
  waiting: "Thinking…",
  speaking: "Speaking",
};

/** Mic button for the composer toolbar: switches voice mode on and off. */
export function VoiceButton() {
  const status = useVoiceStatus().data;
  const phase = useVoiceState((s) => s.phase);
  if (!status?.stt) return null;
  const on = phase !== "off";
  return (
    <button
      type="button"
      onClick={() => (on ? voice.stop() : voice.start())}
      className={clsx(
        "rounded-lg p-1.5 hover:bg-zinc-100 dark:hover:bg-zinc-800",
        on ? "text-red-600 dark:text-red-400" : "text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100",
      )}
      aria-label={on ? "End voice mode" : "Voice mode"}
      title={on ? "End voice mode" : "Talk instead of typing"}
    >
      {on ? <MicOff size={18} /> : <Mic size={18} />}
    </button>
  );
}

/** Status strip shown above the composer while voice mode is on. */
export function VoiceBar() {
  const { phase, error, level } = useVoiceState();
  if (phase === "off" && !error) return null;
  const meter = Math.max(0, Math.min(1, (level + 70) / 50));
  return (
    <div className="mb-2 flex items-center gap-3 rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm dark:border-zinc-800 dark:bg-zinc-900">
      {phase !== "off" && (
        <>
          <span className="relative flex h-3 w-3">
            <span className={clsx("absolute inline-flex h-full w-full rounded-full opacity-60", phase === "hearing" ? "animate-ping bg-red-500" : phase === "speaking" ? "animate-ping bg-indigo-500" : "bg-zinc-400")} />
            <span className={clsx("relative inline-flex h-3 w-3 rounded-full", phase === "hearing" ? "bg-red-500" : phase === "speaking" ? "bg-indigo-500" : "bg-zinc-400")} />
          </span>
          <span className="min-w-0 flex-1 truncate">{LABELS[phase]}</span>
          <span className="hidden h-1.5 w-24 overflow-hidden rounded-full bg-zinc-200 sm:block dark:bg-zinc-800">
            <span className="block h-full bg-green-500 transition-[width] duration-75" style={{ width: `${meter * 100}%` }} />
          </span>
          {phase === "speaking" && (
            <button type="button" onClick={() => voice.interrupt()} className="flex items-center gap-1 rounded-md px-2 py-0.5 text-xs hover:bg-zinc-200 dark:hover:bg-zinc-800">
              <VolumeX size={14} /> Stop
            </button>
          )}
          <button type="button" onClick={() => voice.stop()} className="rounded-md px-2 py-0.5 text-xs text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950">
            End
          </button>
        </>
      )}
      {error && <span className="text-xs text-red-600 dark:text-red-400">{error}</span>}
    </div>
  );
}

/** Speaker button on a message. */
export function ReadAloud({ text }: { text: string }) {
  const status = useVoiceStatus().data;
  if (!status?.tts || !text.trim()) return null;
  return (
    <button
      type="button"
      onClick={() => voice.readAloud(text)}
      className={ACTION_BUTTON}
      aria-label="Read aloud"
      title="Read aloud"
    >
      <Volume2 className={ACTION_ICON} />
    </button>
  );
}

/**
 * Connects voice mode to a chat page: what the user says is sent with `send`, the newest assistant
 * reply is spoken, and talking over it calls `stop`.
 */
export function useVoiceChat(
  send: (text: string) => Promise<unknown> | void,
  stop: () => void,
  reply: { id: string; text: string; streaming: boolean } | null,
) {
  const sendRef = useRef(send);
  const stopRef = useRef(stop);
  sendRef.current = send;
  stopRef.current = stop;
  useEffect(() => voice.register((t) => void sendRef.current(t), () => stopRef.current()), []);
  useEffect(() => {
    if (reply) voice.follow(reply.id, reply.text, !reply.streaming);
  }, [reply?.id, reply?.text, reply?.streaming]);
}
